'use client';

import React, { useMemo } from 'react';
import {
    Box,
    Flex,
    Text,
    Modal,
    Button,
    Checkbox,
    HStack,
    Divider,
    CloseButton,
    Heading,
    Icons,
} from '@shared/ui';
import { ProductCard } from 'app/components/ProductCard';
import i18n from '@shared/i18n';
import { SpecSelection } from '../SpecSelection';
import { getPriceRange, createAddToCartObj, checkProductSoldOut } from './utils';

interface LaptopModalProps {
    visible: boolean;
    handleClose: () => void;
    handleSubmit: () => void;
    isLoading: boolean;
    parentProduct: BuyerAddToCartPayload | null;
    parentProductDetail: Product | null;
    parentProductInfo: {
        name: string;
        image: ProductDisplayMedia;
        price: Price | undefined;
    } | null;
    promoProductDateInfo: PromoProductDateInfo | undefined;
    countdown: string | null;
    dateInfoText: string | null;
    promoProductDetailMap: Record<string, FormattedBuyerPromoProduct>;
    promoProductPayloadMap: Record<string, ReturnType<typeof createAddToCartObj>>;
    selectedGiveawayProductMap: Record<string, unknown>;
    selectedGiveawayProductSpecMap: Record<string, string[]>;
    selectedQuantity: Record<string, number>;
    updateParentProductSpec: (spec_detail_id: string) => void;
    updateParentProductCount: (quantity: number) => void;
    removeAllGiveawayProduct: () => void;
    updateSelectedGiveawayProduct: (productId: string) => void;
    updatePromoProduct: (productId: string, quantity: number) => void;
    updateSelectedGiveawayProductSpec: (
        productId: string,
        index: number,
        specDetailId: string
    ) => void;
    isAllSpecsSelected: () => boolean;
    isEdit: boolean;
    isSubmitting: boolean;
}

export const LaptopModal: React.FC<LaptopModalProps> = ({
    visible,
    isLoading,
    parentProduct,
    parentProductInfo,
    parentProductDetail,
    promoProductDateInfo,
    countdown,
    dateInfoText,
    promoProductDetailMap,
    promoProductPayloadMap,
    selectedGiveawayProductMap,
    selectedGiveawayProductSpecMap,
    selectedQuantity,
    handleClose,
    handleSubmit,
    updateParentProductCount,
    removeAllGiveawayProduct,
    updateSelectedGiveawayProduct,
    updatePromoProduct,
    updateSelectedGiveawayProductSpec,
    isAllSpecsSelected,
    updateParentProductSpec,
    isEdit,
    isSubmitting,
}) => {
    const parentProductSelectedSpecs = useMemo(() => {
        return (
            parentProductDetail?.spec_details?.filter(
                (detail) => detail.id === parentProduct?.spec_detail_id
            ) || []
        );
    }, [parentProduct, parentProductDetail]);

    if (!parentProductDetail) {
        return null;
    }

    return (
        <Modal
            sx={{ maxW: '546px' }}
            isOpen={visible}
            onClose={handleClose}
            closeOnOverlayClick
        >
            <Box p="m">
                <CloseButton
                    position="absolute"
                    top="16px"
                    left="16px"
                    w="16px"
                    h="16px"
                    onClick={handleClose}
                />
                <Heading
                    as="h3"
                    fontSize="sm"
                    mb="s"
                    fontWeight="bold"
                    textAlign="center"
                >
                    {i18n.gettext('加購優惠')}
                </Heading>
                <Divider color="primary.020" />
                <Box
                    maxH={{
                        sm: 'unset',
                        md: '600px',
                    }}
                    overflowY="scroll"
                >
                    {(promoProductDateInfo?.fixed_date !== 0 ||
                        promoProductDateInfo?.fixed_weekday !== 0) &&
                        countdown && (
                            <>
                                <Flex
                                    justifyContent="center"
                                    alignItems="center"
                                    py="xxs"
                                    px="xs"
                                    bg="secondary.010"
                                    color="secondary.050"
                                    gap="xxs"
                                >
                                    <Icons.csr.Alarm size="16px" weight="bold" />
                                    <Text fontWeight="bold" color="secondary.050">
                                        {i18n
                                            .gettext('活動倒數 {countdown}')
                                            .replace('{countdown}', countdown)}
                                    </Text>
                                </Flex>
                                <Flex my="15px" bg="neutral.010" py="11px" px="15px">
                                    {i18n
                                        .gettext(
                                            '購買指定商品即可加購優惠品項，活動至 {當天日期} 止'
                                        )
                                        .replace(
                                            '{當天日期}',
                                            new Date().toLocaleDateString('zh-TW', {
                                                month: 'numeric',
                                                day: 'numeric',
                                            })
                                        )}
                                </Flex>
                            </>
                        )}
                    {!isLoading && dateInfoText && (
                        <Flex mt="15px" bg="neutral.010" py="11px" px="15px">
                            <Text fontSize="base">{dateInfoText}</Text>
                        </Flex>
                    )}
                    <Flex flexDirection="column" mt="15px" gap="m">
                        {parentProduct && parentProductInfo && (
                            <Box>
                                <ProductCard.CartGiveaway
                                    productId={parentProduct.product_id}
                                    name={parentProductInfo.name}
                                    price={parentProductInfo.price}
                                    quantity={parentProduct.quantity}
                                    image={parentProductInfo.image}
                                    discountedPrice={null}
                                    onChange={(_, quantity) =>
                                        updateParentProductCount(quantity)
                                    }
                                    addOnLimit={9999}
                                />
                                {parentProductDetail.spec_details?.length > 0 && (
                                    <Box my="xs">
                                        <SpecSelection
                                            addonIndex={0}
                                            specDetails={
                                                parentProductDetail.spec_details as BuyerPromoProductSpecDetail[]
                                            }
                                            specMetas={parentProductDetail.spec_metas}
                                            onSelect={(specDetail) =>
                                                updateParentProductSpec(specDetail.id)
                                            }
                                            selectedSpecs={parentProductSelectedSpecs}
                                        />
                                    </Box>
                                )}
                                <Divider mt="xs" color="neutral.030" />
                            </Box>
                        )}
                        <Box position="relative">
                            {isLoading &&
                                Array(4)
                                    .fill(null)
                                    .map((_, idx) => (
                                        <ProductCard.Skeleton
                                            key={'cart-giveaway-skeleton-' + idx}
                                        />
                                    ))}

                            {!isLoading && parentProduct && (
                                <>
                                    <Flex justifyContent="space-between">
                                        <Text fontSize="sm" fontWeight="bold">
                                            {i18n.gettext('加購商品')}
                                        </Text>
                                        {!!Object.keys(selectedGiveawayProductMap)
                                            .length && (
                                            <Box>
                                                <Button
                                                    variant="outline"
                                                    boxShadow="none"
                                                    onClick={removeAllGiveawayProduct}
                                                    border="none"
                                                    color="dynamic.040"
                                                    h="24px !important"
                                                >
                                                    {i18n.gettext('清除')}
                                                </Button>
                                            </Box>
                                        )}
                                    </Flex>
                                    <Box mt="m">
                                        {Object.values(promoProductDetailMap).map(
                                            (product) => {
                                                // 可用於 counter 的最大值判斷，
                                                const addOnLimit = Math.min(
                                                    product.spec_details.length > 0
                                                        ? Infinity
                                                        : product.stock -
                                                              (product.cart_quantity -
                                                                  (selectedQuantity[
                                                                      product.id
                                                                  ] ?? 0)),
                                                    promoProductDetailMap[product.id]
                                                        .add_on_limit *
                                                        parentProduct.quantity
                                                );

                                                const payloadQuantity =
                                                    promoProductPayloadMap[product.id]
                                                        .quantity;

                                                const isSoldOut = checkProductSoldOut(
                                                    product,
                                                    isEdit
                                                );

                                                const isDisabled =
                                                    // 選擇的加架構商品大於主商品數量 and 已達數量上限並且未被選取
                                                    (Object.keys(
                                                        selectedGiveawayProductMap
                                                    ).length >= parentProduct.quantity &&
                                                        !selectedGiveawayProductMap[
                                                            product.id
                                                        ]) ||
                                                    isSoldOut;

                                                const selectedSpecs =
                                                    selectedGiveawayProductSpecMap[
                                                        product.id
                                                    ]?.map((spec) => {
                                                        return product.spec_details.find(
                                                            (detail) => detail.id === spec
                                                        );
                                                    }) as BuyerPromoProductSpecDetail[];

                                                const price =
                                                    getPriceRange(
                                                        selectedSpecs,
                                                        'price'
                                                    ) || product.price;

                                                const addOnPrice =
                                                    getPriceRange(
                                                        selectedSpecs,
                                                        'add_on_price'
                                                    ) || product.add_on_price;

                                                const isChecked =
                                                    !!selectedGiveawayProductMap[
                                                        product.id
                                                    ];

                                                return (
                                                    <Box
                                                        key={
                                                            'cart-giveaway-' + product.id
                                                        }
                                                        sx={{
                                                            opacity: isDisabled ? 0.3 : 1,
                                                            pointerEvents: isDisabled
                                                                ? 'none'
                                                                : 'auto',
                                                        }}
                                                    >
                                                        <HStack>
                                                            <Checkbox
                                                                isChecked={isChecked}
                                                                onChange={() => {
                                                                    updateSelectedGiveawayProduct(
                                                                        product.id
                                                                    );
                                                                }}
                                                                isDisabled={isDisabled}
                                                            />
                                                            <ProductCard.CartGiveaway
                                                                productId={product.id}
                                                                name={product.name}
                                                                badge={product.badge}
                                                                price={price}
                                                                specInfo={
                                                                    product?.specInfo
                                                                }
                                                                quantity={payloadQuantity}
                                                                image={product.image}
                                                                discountedPrice={
                                                                    addOnPrice
                                                                }
                                                                onChange={
                                                                    updatePromoProduct
                                                                }
                                                                addOnLimit={addOnLimit}
                                                                isAddonProduct
                                                                isSoldOut={isSoldOut}
                                                            />
                                                        </HStack>
                                                        {isChecked && (
                                                            <Text
                                                                display="block"
                                                                as="strong"
                                                                color="secondary.060"
                                                                fontSize="base"
                                                                textAlign="right"
                                                                mt="xs"
                                                            >
                                                                {i18n.gettext(
                                                                    '最多購買數量'
                                                                )}
                                                                &nbsp;
                                                                {addOnLimit}
                                                            </Text>
                                                        )}
                                                        {product.spec_details?.length >
                                                            0 && (
                                                            <Box my="xs">
                                                                {Array.from({
                                                                    length: payloadQuantity,
                                                                }).map((_, index) => (
                                                                    <Box
                                                                        key={`${product.id}-${index}`}
                                                                    >
                                                                        {payloadQuantity >
                                                                            1 && (
                                                                            <Box
                                                                                py="xxs"
                                                                                px="s"
                                                                                bg="secondary.010"
                                                                                borderBottom="1px solid"
                                                                                borderColor="secondary.020"
                                                                            >
                                                                                <Text color="secondary.060">
                                                                                    {i18n
                                                                                        .gettext(
                                                                                            '第 {index} 件加購品'
                                                                                        )
                                                                                        .replace(
                                                                                            '{index}',
                                                                                            String(
                                                                                                index +
                                                                                                    1
                                                                                            )
                                                                                        )}
                                                                                </Text>
                                                                            </Box>
                                                                        )}
                                                                        <SpecSelection
                                                                            key={index}
                                                                            addonIndex={
                                                                                index
                                                                            }
                                                                            specDetails={
                                                                                product.spec_details
                                                                            }
                                                                            specMetas={
                                                                                product.spec_metas
                                                                            }
                                                                            onSelect={(
                                                                                specDetail
                                                                            ) =>
                                                                                updateSelectedGiveawayProductSpec(
                                                                                    product.id,
                                                                                    index,
                                                                                    specDetail.id
                                                                                )
                                                                            }
                                                                            selectedSpecs={
                                                                                selectedSpecs
                                                                            }
                                                                        />
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            }
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Flex>
                </Box>
                <Divider mb="m" color="primary.020" />
                <Flex h="40px">
                    <Button
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        isDisabled={
                            !Object.keys(selectedGiveawayProductMap).length ||
                            !isAllSpecsSelected()
                        }
                    >
                        {i18n.gettext('確定加購')}
                    </Button>
                </Flex>
            </Box>
        </Modal>
    );
};
